import { useLayoutEffect, useRef, useState } from 'react';
import { getTranslations } from '../utils/translations';

/**
 * Season-level synopsis shown under the video, above the episode metadata.
 *
 * Intentionally NOT built on ReadMoreText: that component syncs its expand state
 * to the single shared TextExpansionContext (isTextExpanded), so a second instance
 * would toggle in lockstep with the episode description. This keeps its own local
 * state so the two are independent.
 *
 * Collapsed view is clamped to MAX_LINES rendered lines (not a character count),
 * so the "+ Read More" toggle always sits on the line after the clamp regardless
 * of viewport width. The toggle only renders when the text actually overflows.
 */
const MAX_LINES = 2;

function SeriesSynopsis({ text, textColor = '#fff' }) {
    const t = getTranslations();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef(null);

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el) return undefined;

        const measure = () => {
            // scrollHeight > clientHeight only while the clamp is hiding lines,
            // so measure against an unexpanded state.
            setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
        };

        if (!isExpanded) measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [text, isExpanded]);

    if (!text) return null;

    const toggle = () => setIsExpanded((v) => !v);

    return (
        <div id="series-synopsis" style={{ color: textColor, padding: '8px 0 0', textAlign: 'left', lineHeight: 1.5 }}>
            <div
                ref={textRef}
                style={isExpanded ? undefined : {
                    display: '-webkit-box',
                    WebkitLineClamp: MAX_LINES,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
            >
                {text}
            </div>
            {(isOverflowing || isExpanded) && (
                <a
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.preventDefault(); toggle(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                    style={{ color: textColor, textDecoration: 'none', cursor: 'pointer', fontWeight: 'normal' }}
                >
                    {isExpanded ? `- ${t.showLess}` : `+ ${t.readMore}`}
                </a>
            )}
        </div>
    );
}

export default SeriesSynopsis;
